import React, { Component } from "react";
import MetaTags from "react-meta-tags";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Alert, Container } from "reactstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  userChangePassword,
  userChangePasswordError,
  getPasswordHistory,
} from "store/auth/changepwd/actions";

class ChangePassword extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showOld: false,
      showNew: false,
      showConfirm: false,
      strength: 0,
      changeSuccessMsg: "",
      passwordFieldError: "",
      userId: null,
    };
  }

  componentDidMount() {
    const user = JSON.parse(localStorage.getItem("authUser"));
    if (user) {
      this.setState({ userId: user.user_id }, () => {
        this.props.getPasswordHistory(this.state.userId);
      });
    }
    this.props.userChangePasswordError("");
  }

  getStrength = password => {
    if (!password) return 0;
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score;
  };

  getStrengthInfo = score => {
    if (score === 0) return { label: "", color: "#e5e7eb" };
    if (score === 1) return { label: "Weak", color: "#E24B4A" };
    if (score === 2) return { label: "Fair", color: "#EF9F27" };
    if (score === 3) return { label: "Good", color: "#639922" };
    return { label: "Strong", color: "#1D9E75" };
  };

  render() {
    const { passwordHistory, historyLoading } = this.props;
    const {
      showOld,
      showNew,
      showConfirm,
      strength,
      changeSuccessMsg,
      passwordFieldError,
    } = this.state;
    const strengthInfo = this.getStrengthInfo(strength);

    return (
      <React.Fragment>
        <div className="page-content">
          <MetaTags>
            <title>NHS NEQAS | Change Password</title>
          </MetaTags>
          <Container fluid>
            <Breadcrumbs title="Account" breadcrumbItem="Change password" />

            {changeSuccessMsg && (
              <Alert color="success" className="mb-3" style={{ maxWidth: 960 }}>
                {changeSuccessMsg}
              </Alert>
            )}

            {/* ── Main Card ── */}
            <div className="d-flex justify-content-center">
              <div
                className="border rounded-3 overflow-hidden bg-white w-100"
                style={{ maxWidth: 960 }}
              >
                {/* ── Header ── */}
                <div className="d-flex align-items-center gap-2 px-4 py-3 border-bottom bg-light">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-2 me-1"
                    style={{
                      width: 38,
                      height: 38,
                      background: "#e8f0fe",
                      flexShrink: 0,
                    }}
                  >
                    <i className="mdi mdi-lock-outline fs-5 text-primary" />
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="mb-0 fw-semibold">Change password</h6>
                    <p className="mb-0 text-muted" style={{ fontSize: 14 }}>
                      Keep your account secure with a strong password.
                    </p>
                  </div>
                  <span
                    className="d-inline-flex align-items-center gap-1 badge rounded-pill"
                    style={{
                      background: "#ecfdf5",
                      color: "#065f46",
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    <i className="mdi mdi-shield-check-outline" /> Secured
                  </span>
                </div>

                {/* ── Body: Split Panel ── */}
                <div className="row g-0">
                  {/* Left — Form */}
                  <div className="col-6 p-5 bg-light">
                    <Formik
                      enableReinitialize
                      initialValues={{
                        old_password: "",
                        new_password: "",
                        new_password2: "",
                      }}
                      validationSchema={Yup.object().shape({
                        old_password: Yup.string().required(
                          "Enter your current password"
                        ),
                        new_password: Yup.string().required(
                          "Enter a new password"
                        ),
                        new_password2: Yup.string()
                          .required("Confirm your new password")
                          .when("new_password", {
                            is: val => val && val.length > 0,
                            then: Yup.string().oneOf(
                              [Yup.ref("new_password")],
                              "Passwords don't match"
                            ),
                          }),
                      })}
                      onSubmit={(values, { resetForm }) => {
                        const payload = {
                          ...values,
                          user_id: this.state.userId,
                        };
                        this.props.userChangePassword(payload);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                        setTimeout(() => {
                          this.setState({
                            passwordFieldError: this.props.changeError,
                            changeSuccessMsg: this.props.changeSuccessMsg,
                          });
                        }, 1000);
                        setTimeout(() => {
                          this.setState({ changeSuccessMsg: "", strength: 0 });
                          resetForm();
                        }, 5000);
                      }}
                    >
                      {({ errors, touched, setFieldValue }) => (
                        <Form>
                          {/* Current password */}
                          <div className="mb-3">
                            <label
                              className="form-label fw-semibold"
                              style={{ fontSize: 14 }}
                            >
                              Current password
                            </label>
                            <div className="input-group">
                              <Field
                                name="old_password"
                                type={showOld ? "text" : "password"}
                                placeholder="Enter current password"
                                onFocus={() =>
                                  this.setState({ passwordFieldError: "" })
                                }
                                className={
                                  "form-control form-control-sm" +
                                  ((errors.old_password &&
                                    touched.old_password) ||
                                  passwordFieldError
                                    ? " is-invalid"
                                    : "")
                                }
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() =>
                                  this.setState({ showOld: !showOld })
                                }
                              >
                                <i
                                  className={`mdi ${
                                    showOld
                                      ? "mdi-eye-off-outline"
                                      : "mdi-eye-outline"
                                  }`}
                                />
                              </button>
                            </div>
                            <ErrorMessage
                              name="old_password"
                              component="div"
                              className="text-danger mt-1"
                              style={{ fontSize: 14 }}
                            />
                            {passwordFieldError && (
                              <div
                                className="text-danger mt-1"
                                style={{ fontSize: 14 }}
                              >
                                {passwordFieldError}
                              </div>
                            )}
                          </div>

                          {/* New password */}
                          <div className="mb-3">
                            <label
                              className="form-label fw-semibold"
                              style={{ fontSize: 14 }}
                            >
                              New password
                            </label>
                            <div className="input-group">
                              <Field
                                name="new_password"
                                type={showNew ? "text" : "password"}
                                placeholder="Enter new password"
                                className={
                                  "form-control form-control-sm" +
                                  (errors.new_password && touched.new_password
                                    ? " is-invalid"
                                    : "")
                                }
                                onChange={e => {
                                  setFieldValue("new_password", e.target.value);
                                  this.setState({
                                    strength: this.getStrength(e.target.value),
                                  });
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() =>
                                  this.setState({ showNew: !showNew })
                                }
                              >
                                <i
                                  className={`mdi ${
                                    showNew
                                      ? "mdi-eye-off-outline"
                                      : "mdi-eye-outline"
                                  }`}
                                />
                              </button>
                            </div>

                            {/* Strength bar */}
                            <div className="d-flex align-items-center gap-1 mt-2">
                              {[1, 2, 3, 4].map(i => (
                                <div
                                  key={i}
                                  className="rounded-pill flex-grow-1"
                                  style={{
                                    height: 3,
                                    background:
                                      strength >= i
                                        ? strengthInfo.color
                                        : "#e5e7eb",
                                    transition: "background 0.2s",
                                  }}
                                />
                              ))}
                              {strength > 0 && (
                                <span
                                  className="fw-semibold ms-1"
                                  style={{
                                    fontSize: 14,
                                    color: strengthInfo.color,
                                    minWidth: 40,
                                  }}
                                >
                                  {strengthInfo.label}
                                </span>
                              )}
                            </div>

                            <ErrorMessage
                              name="new_password"
                              component="div"
                              className="text-danger mt-1"
                              style={{ fontSize: 14 }}
                            />
                          </div>

                          {/* Confirm password */}
                          <div className="mb-4">
                            <label
                              className="form-label fw-semibold"
                              style={{ fontSize: 14 }}
                            >
                              Confirm new password
                            </label>
                            <div className="input-group">
                              <Field
                                name="new_password2"
                                type={showConfirm ? "text" : "password"}
                                placeholder="Re-enter new password"
                                className={
                                  "form-control form-control-sm" +
                                  (errors.new_password2 && touched.new_password2
                                    ? " is-invalid"
                                    : "")
                                }
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() =>
                                  this.setState({ showConfirm: !showConfirm })
                                }
                              >
                                <i
                                  className={`mdi ${
                                    showConfirm
                                      ? "mdi-eye-off-outline"
                                      : "mdi-eye-outline"
                                  }`}
                                />
                              </button>
                            </div>
                            <ErrorMessage
                              name="new_password2"
                              component="div"
                              className="text-danger mt-1"
                              style={{ fontSize: 14 }}
                            />
                          </div>

                          <div className="d-flex justify-content-end">
                            <button
                              type="submit"
                              className="btn btn-primary btn-sm px-4"
                            >
                              Update password
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </div>

                  {/* Right — History */}
                  <div className="col-6 p-4 bg-light">
                    <p
                      className="text-uppercase text-muted fw-semibold mb-3"
                      style={{ fontSize: 14, letterSpacing: "0.5px" }}
                    >
                      Change history
                    </p>

                    {historyLoading ? (
                      <p className="text-muted" style={{ fontSize: 14 }}>
                        Loading...
                      </p>
                    ) : passwordHistory && passwordHistory.length > 0 ? (
                      <div>
                        {passwordHistory.map((entry, idx) => (
                          <div
                            key={entry.id}
                            className={`d-flex gap-2 py-2 ${
                              idx < passwordHistory.length - 1
                                ? "border-bottom"
                                : ""
                            }`}
                          >
                            <div
                              className="rounded-circle mt-1 flex-shrink-0"
                              style={{
                                width: 7,
                                height: 7,
                                background: "#1a56db",
                                marginTop: 5,
                              }}
                            />
                            <div>
                              <div
                                className="fw-semibold"
                                style={{ fontSize: 14 }}
                              >
                                {entry.changed_by}
                              </div>
                              <div
                                className="text-muted"
                                style={{ fontSize: 14 }}
                              >
                                {new Date(entry.changed_at).toLocaleString(
                                  "en-GB",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                )}
                              </div>
                              <div
                                className="d-flex flex-column gap-0 mt-1 text-muted"
                                style={{ fontSize: 14 }}
                              >
                                <span>
                                  Old: <strong>{entry.old_password}</strong>
                                </span>
                                <span>
                                  New: <strong>{entry.new_password}</strong>
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted" style={{ fontSize: 14 }}>
                        No changes recorded yet.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

ChangePassword.propTypes = {
  changeError: PropTypes.any,
  changeSuccessMsg: PropTypes.string,
  history: PropTypes.object,
  userChangePassword: PropTypes.func,
  userChangePasswordError: PropTypes.func,
  getPasswordHistory: PropTypes.func,
  passwordHistory: PropTypes.array,
  historyLoading: PropTypes.bool,
};

const mapStateToProps = state => {
  const { changeError, changeSuccessMsg, passwordHistory, historyLoading } =
    state.ChangePassword;
  return { changeError, changeSuccessMsg, passwordHistory, historyLoading };
};

export default withRouter(
  connect(mapStateToProps, {
    userChangePassword,
    userChangePasswordError,
    getPasswordHistory,
  })(ChangePassword)
);
